import React, { useEffect, useState, useRef } from "react";
import {
  Button,
  Drawer,
  IconButton,
  useMediaQuery,
  Menu,
  Paper,
  List,
  ListItemButton,
  CircularProgress,
} from "@mui/material";
import {
  MuiBox,
  MuiIconButton,
  MuiTypography,
  SearchInput,
} from "components/atoms";
import { FilterMenu, UserProfile } from "components/molecules";
import DriveSidebar from "../DriveSidebar";
import { useLocation, useNavigate, useSearchParams } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { useDispatch, useSelector } from "react-redux";
import MenuIcon from "@mui/icons-material/Menu";
import UpdateIcon from "@mui/icons-material/Update";
import { Close } from "@mui/icons-material";

import CloseIcon from "assets/images/svg-icons/closeIcon.svg";
import FilterIcon from "assets/images/svg-icons/filter.svg";

import {
  GetSearchHistoryAction,
  ClearSearchHistoryAction,
  AddSearchHistoryAction,
} from "store/middlewares/search";

const DriveHeader = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const isMdDown = useMediaQuery("(max-width:1200px)");
  const [open, setOpen] = useState(false);
  const location = useLocation();
  const isSettings = location.pathname.startsWith("/drive/settings");
  const [anchorEl, setAnchorEl] = useState(null);
  const openfilter = Boolean(anchorEl);
  const [searchParams] = useSearchParams();
  const searchValue = searchParams.get("search");
  const [search, setSearch] = useState(searchValue ?? "");

  const [showDropdown, setShowDropdown] = useState(false);
  const wrapperRefDesktop = useRef(null);
  const wrapperRefMobile = useRef(null);

  const { searchHistory, searchHistoryLoading } = useSelector(
    (state) => state.general,
  );

  // GET response is an array: [{ title, id }, ...]
  const historyList = Array.isArray(searchHistory) ? searchHistory : [];

  // Fetch search history once when header mounts
  useEffect(() => {
    dispatch(GetSearchHistoryAction());
  }, [dispatch]);

  useEffect(() => {
    if (!location.pathname.startsWith("/drive/search")) {
      setSearch("");
    }
  }, [location.pathname]);

  useEffect(() => {
    if (isMdDown) {
      setOpen(false);
    }
  }, [location.key, isMdDown]);

  useEffect(() => {
    function handleClickOutside(event) {
      const inDesktop =
        wrapperRefDesktop.current &&
        wrapperRefDesktop.current.contains(event.target);
      const inMobile =
        wrapperRefMobile.current &&
        wrapperRefMobile.current.contains(event.target);
      if (!inDesktop && !inMobile) {
        setShowDropdown(false);
      }
    }

    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  useEffect(() => {
    const term = search?.trim();
    if (!term) return;

    const timer = setTimeout(() => {
      dispatch(AddSearchHistoryAction({ title: term }));
    }, 2000);

    return () => clearTimeout(timer);
  }, [search, dispatch]);

  const handleOpenDropdown = () => {
    setShowDropdown(true);
  };

  const handleSearchKey = (e) => {
    if (e.key === "Enter") {
      e.preventDefault();
      const term = search?.trim();
      if (term) {
        dispatch(AddSearchHistoryAction({ title: term }));
        navigate(`/drive/search?search=${term}`);
      } else {
        navigate(`/drive/search?search=`);
      }
      setShowDropdown(false);
    }
  };

  const handleSearch = () => {
    const term = search?.trim();
    if (term) {
      dispatch(AddSearchHistoryAction({ title: term }));
      navigate(`/drive/search?search=${term}`);
    }
    setShowDropdown(false);
  };

  const handleClick = (event) => {
    setAnchorEl(event.currentTarget);
  };

  const handleClose = () => {
    setAnchorEl(null);
  };

  const handleSelectSearch = (term) => {
    setSearch(term);
    navigate(`/drive/search?search=${term}`);
    setShowDropdown(false);
  };

  // Clear all → type: "all", no id
  const handleClearAllHistory = () => {
    dispatch(ClearSearchHistoryAction({ type: "all" }));
  };

  // Clear single → type: "single" + id
  const handleDeleteHistoryItem = (id) => {
    dispatch(ClearSearchHistoryAction({ type: "single", id }));
  };

  const handleSearchChange = (e) => {
    setSearch(e.target.value);
    setShowDropdown(true);
  };

  const renderSearchHistoryDropdown = () => {
    if (!showDropdown) return null;

    return (
      <Paper
        elevation={2}
        sx={{
          position: "absolute",
          top: "100%",
          left: 0,
          width: "100%",
          maxHeight: "300px",
          overflowY: "auto",
          mt: "4px",
          borderRadius: "8px",
          border: "4px solid",
          borderColor: "background.gray400",
          zIndex: 20,
          boxShadow: "none",
        }}
        className="no-scrollbar"
      >
        {historyList.length > 0 && (
          <MuiBox
            sx={{
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
              px: 2,
              py: 1,
            }}
          >
            <MuiTypography
              fontSize="14px"
              color="text.lightgray"
              fontWeight="500"
            >
              {t("header.recentSearches")}
            </MuiTypography>
            <MuiTypography
              onMouseDown={(e) => {
                e.preventDefault();
                e.stopPropagation();
                handleClearAllHistory();
              }}
              sx={{ cursor: "pointer" }}
              fontSize="14px"
              color="text.ottomanRed"
              fontWeight="500"
            >
              {t("header.clear")}
            </MuiTypography>
          </MuiBox>
        )}

        {searchHistoryLoading ? (
          <MuiBox
            sx={{
              display: "flex",
              justifyContent: "center",
              py: 2,
            }}
          >
            <CircularProgress size={20} />
          </MuiBox>
        ) : historyList.length > 0 ? (
          <List sx={{ p: 0 }}>
            {historyList.map((item) => (
              <ListItemButton
                key={item.id}
                sx={{
                  display: "flex",
                  justifyContent: "space-between",
                  alignItems: "center",
                  py: "6px",
                  px: "14px",
                  "&:hover": {
                    backgroundColor: "#F5F5F5",
                  },
                }}
                onClick={() => handleSelectSearch(item.title)}
              >
                <MuiBox
                  sx={{
                    display: "flex",
                    alignItems: "center",
                    gap: "8px",
                    flex: 1,
                    minWidth: 0,
                  }}
                >
                  <MuiBox sx={{ width: "20px", height: "20px", flexShrink: 0 }}>
                    <UpdateIcon sx={{ color: "text.contrast" }} />
                  </MuiBox>
                  <MuiTypography
                    fontSize="14px"
                    color="text.lightgray"
                    fontWeight="500"
                    sx={{
                      whiteSpace: "nowrap",
                      overflow: "hidden",
                      textOverflow: "ellipsis",
                    }}
                  >
                    {item.title}
                  </MuiTypography>
                </MuiBox>

                <IconButton
                  sx={{
                    width: "24px",
                    height: "24px",
                    p: "5px",
                    borderRadius: "8px",
                    color: "text.ottomanRed",
                  }}
                  onMouseDown={(e) => {
                    e.preventDefault();
                    e.stopPropagation();
                  }}
                  onClick={(e) => {
                    e.preventDefault();
                    e.stopPropagation();
                    handleDeleteHistoryItem(item.id);
                  }}
                >
                  <Close />
                </IconButton>
              </ListItemButton>
            ))}
          </List>
        ) : (
          <MuiBox sx={{ p: "6px 12px" }}>
            <MuiTypography
              fontSize="14px"
              color="text.lightgray"
              fontWeight="500"
            >
              {t("header.noSuggestionsFound")}
            </MuiTypography>
          </MuiBox>
        )}
      </Paper>
    );
  };

  const searchEndAdornment = (
    <MuiBox
      sx={{
        width: "fit-content",
        display: "flex",
        gap: "12px",
        alignItems: "center",
      }}
    >
      {search && (
        <MuiIconButton
          size={"18px"}
          src={CloseIcon}
          onClick={() => {
            setSearch("");
            if (location.pathname.includes("/drive/search")) {
              navigate("/drive/search");
            }
          }}
        />
      )}
      <Button
        onClick={handleClick}
        variant="text"
        sx={{
          color: "text.darkgray",
          fontSize: "14px",
          fontWeight: "500",
          p: 0,
          mr: "16px",
          ":hover": {
            backgroundColor: "transparent",
          },
        }}
        startIcon={
          <MuiBox
            sx={{
              height: "14px",
              width: "14px",
              display: "flex",
            }}
          >
            <img src={FilterIcon} alt="FilterIcon" className="img-full" />
          </MuiBox>
        }
      >
        {t("header.filter")}
      </Button>
    </MuiBox>
  );

  return (
    <MuiBox
      sx={{
        p: "16px 16px 0 16px",
        display: "flex",
        alignItems: "center",
        justifyContent: "space-between",
        gap: "16px",
        backgroundColor: "background.gray200",
        flexWrap: "wrap",
      }}
    >
      <MuiBox
        sx={{ display: "flex", alignItems: "center", gap: "8px", flex: 1 }}
      >
        {isMdDown && (
          <IconButton onClick={() => setOpen(true)}>
            <MenuIcon sx={{ color: "black", fontSize: "32px" }} />
          </IconButton>
        )}
        {!isSettings && (
          <>
            {/* Desktop search with history */}
            <MuiBox
              sx={{
                maxWidth: "600px",
                width: "100%",
                display: { xs: "none", sm: "flex" },
                gap: "6px",
              }}
            >
              <div
                ref={wrapperRefDesktop}
                style={{ position: "relative", width: "100%" }}
              >
                <SearchInput
                  isBold={true}
                  sx={{ height: "40px" }}
                  placeholder={t("header.searchInDrive")}
                  value={search}
                  onChange={handleSearchChange}
                  onFocus={handleOpenDropdown}
                  onKeyDown={handleSearchKey}
                  onBlur={() => {
                    setTimeout(() => {
                      if (
                        wrapperRefDesktop.current &&
                        !wrapperRefDesktop.current.contains(
                          document.activeElement,
                        )
                      ) {
                        setShowDropdown(false);
                      }
                    }, 150);
                  }}
                  endAdornment={searchEndAdornment}
                />
                {renderSearchHistoryDropdown()}
              </div>
              {search && (
                <Button
                  variant="blueContained"
                  onClick={handleSearch}
                  sx={{ height: "40px" }}
                >
                  {t("commonButton.search")}
                </Button>
              )}
            </MuiBox>
            <Menu
              anchorEl={anchorEl}
              open={openfilter}
              onClose={handleClose}
              MenuListProps={{
                disablePadding: true,
              }}
              slotProps={{
                paper: {
                  sx: {
                    width: "calc(100vw - 32px)",
                    maxWidth: "600px",
                    mt: "12px",
                    p: 0,
                    boxShadow: "0px 6px 24px 0px #0000000D",
                    borderRadius: "8px",
                    border: "1px solid",
                    borderColor: "background.gray400",
                  },
                },
              }}
            >
              <FilterMenu onClose={handleClose} />
            </Menu>
          </>
        )}
      </MuiBox>
      <UserProfile />
      <Drawer anchor="left" open={open} onClose={() => setOpen(false)}>
        <DriveSidebar onMenuClick={() => setOpen(false)} />
      </Drawer>
      {!isSettings && (
        <>
          {/* Mobile search with history */}
          <MuiBox
            sx={{
              maxWidth: "600px",
              width: "100%",
              display: { xs: "flex", sm: "none" },
              gap: "6px",
            }}
          >
            <div
              ref={wrapperRefMobile}
              style={{ position: "relative", width: "100%" }}
            >
              <SearchInput
                isBold={true}
                sx={{ height: "40px" }}
                placeholder={t("header.searchInDrive")}
                value={search}
                onChange={handleSearchChange}
                onFocus={handleOpenDropdown}
                onBlur={() => {
                  setTimeout(() => {
                    if (
                      wrapperRefMobile.current &&
                      !wrapperRefMobile.current.contains(document.activeElement)
                    ) {
                      setShowDropdown(false);
                    }
                  }, 150);
                }}
                endAdornment={searchEndAdornment}
              />
              {renderSearchHistoryDropdown()}
            </div>
            {search && (
              <Button
                variant="blueContained"
                onClick={handleSearch}
                sx={{ height: "40px" }}
              >
                {t("commonButton.search")}
              </Button>
            )}
          </MuiBox>
          <Menu
            anchorEl={anchorEl}
            open={openfilter}
            onClose={handleClose}
            MenuListProps={{
              disablePadding: true,
            }}
            slotProps={{
              paper: {
                sx: {
                  width: "calc(100vw - 32px)",
                  maxWidth: "600px",
                  mt: "12px",
                  p: 0,
                  boxShadow: "0px 6px 24px 0px #0000000D",
                  borderRadius: "8px",
                  border: "1px solid",
                  borderColor: "background.gray400",
                },
              },
            }}
          >
            <FilterMenu onClose={handleClose} />
          </Menu>
        </>
      )}
    </MuiBox>
  );
};

export default DriveHeader;
